import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { Menu, Sparkles, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { useNotification } from '../../contexts/NotificationContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useAiAssistantEnabled, fetchAssistantModels } from '../ai/useAiAssistant';
import type { AiModelOption } from '../configuration-settings';
import ConversationSidebar from './components/ConversationSidebar';
import ChatPanel from './components/ChatPanel';
import {
  CONVERSATIONS_KEY,
  conversationMessagesKey,
  createAiConversation,
  deleteAiConversation,
  sendAiChatMessage,
  useAiChatConversations,
  useAiChatMessages,
} from './useAiChat';
import type { AiChatMessage } from './types';

const AiChatPage: React.FC = () => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { mutate: globalMutate } = useSWRConfig();
  const enabled = useAiAssistantEnabled();
  const isMobileLayout = useMediaQuery('(max-width: 768px)');
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const raw = searchParams.get('id');
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });
  const [models, setModels] = useState<AiModelOption[]>([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobileLayout);
  const [optimisticMessages, setOptimisticMessages] = useState<AiChatMessage[]>([]);

  const { data: convData, isLoading: loadingConversations, mutate: mutateConversations } =
    useAiChatConversations(enabled);
  const conversations = convData?.conversations ?? [];

  const { data: msgData, isLoading: loadingMessages, mutate: mutateMessages } = useAiChatMessages(
    selectedId,
    enabled,
  );

  useEffect(() => {
    if (!enabled) return;
    void fetchAssistantModels()
      .then((list) => {
        setModels(list);
        if (list.length > 0) {
          const def = list.find((m) => m.defaultModel) ?? list[0];
          setSelectedModelId(def.id);
        }
      })
      .catch(() => {
        showError('Could not load AI models');
      });
  }, [enabled, showError]);

  useEffect(() => {
    const raw = searchParams.get('id');
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    if (Number.isFinite(parsed)) {
      setSelectedId(parsed);
      if (isMobileLayout) setSidebarOpen(false);
    }
  }, [searchParams, isMobileLayout]);

  useEffect(() => {
    setSidebarOpen(!isMobileLayout);
  }, [isMobileLayout]);

  useEffect(() => {
    if (selectedId || conversations.length === 0) return;
    setSelectedId(conversations[0].id);
  }, [conversations, selectedId]);

  useEffect(() => {
    setOptimisticMessages([]);
  }, [selectedId]);

  const syncSelectedId = useCallback(
    (id: number | null) => {
      setSelectedId(id);
      if (id != null) {
        setSearchParams({ id: String(id) }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
      if (isMobileLayout && id != null) setSidebarOpen(false);
    },
    [isMobileLayout, setSearchParams],
  );

  const handleNewChat = useCallback(async () => {
    if (!enabled) return;
    setCreating(true);
    try {
      const conversation = await createAiConversation(selectedModelId || undefined);
      await mutateConversations();
      syncSelectedId(conversation.id);
      void globalMutate(conversationMessagesKey(conversation.id));
      showSuccess('New chat started');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Could not create chat');
    } finally {
      setCreating(false);
    }
  }, [enabled, globalMutate, mutateConversations, selectedModelId, showError, showSuccess, syncSelectedId]);

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        await deleteAiConversation(id);
        if (selectedId === id) {
          syncSelectedId(null);
        }
        await mutateConversations();
        void globalMutate(conversationMessagesKey(id));
        showSuccess('Conversation deleted');
      } catch {
        showError('Could not delete conversation');
      }
    },
    [globalMutate, mutateConversations, selectedId, showError, showSuccess, syncSelectedId],
  );

  const handleSend = useCallback(
    async (content: string): Promise<boolean> => {
      if (!selectedId) return false;

      const tempId = -Date.now();
      setOptimisticMessages([
        {
          id: tempId,
          role: 'user',
          content,
          createdAt: new Date().toISOString(),
        },
      ]);
      setSending(true);
      try {
        await sendAiChatMessage(selectedId, content, selectedModelId || undefined);
        setOptimisticMessages([]);
        await Promise.all([mutateMessages(), mutateConversations()]);
        void globalMutate(CONVERSATIONS_KEY);
        return true;
      } catch (err: unknown) {
        setOptimisticMessages([]);
        const message =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        showError(message || 'Could not send message');
        return false;
      } finally {
        setSending(false);
      }
    },
    [globalMutate, mutateConversations, mutateMessages, selectedId, selectedModelId, showError],
  );

  const displayMessages = [...(msgData?.messages ?? []), ...optimisticMessages];

  if (!enabled) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 min-h-[320px] p-6 text-center">
        <Sparkles className={cn('h-10 w-10 mb-3', colors.textMuted)} aria-hidden />
        <p className={cn('text-sm max-w-md', colors.textSecondary)}>
          AI Assistant is disabled. Enable it in Settings → Application → AI assistant (CRM & dashboard).
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {isMobileLayout ? (
        <div className={cn('flex shrink-0 items-center gap-2 border-b px-3 py-2', colors.border, colors.surface)}>
          <button
            type="button"
            onClick={() => setSidebarOpen((v) => !v)}
            className={cn('rounded-lg p-2', colors.interactiveHover, colors.textSecondary)}
            aria-label={sidebarOpen ? 'Hide conversations' : 'Show conversations'}
          >
            {sidebarOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </button>
          <p className={cn('min-w-0 flex-1 truncate text-sm font-semibold', colors.text)}>
            {msgData?.conversation?.title ?? conversations.find((c) => c.id === selectedId)?.title ?? 'AI Chat'}
          </p>
        </div>
      ) : null}

      <div
        className={cn(
          'flex min-h-0 flex-1 overflow-hidden rounded-xl border',
          colors.border,
          colors.surface,
          isMobileLayout && 'rounded-none border-0',
        )}
      >
        {(!isMobileLayout || sidebarOpen) ? (
          <ConversationSidebar
            conversations={conversations}
            selectedId={selectedId}
            loading={loadingConversations}
            onSelect={syncSelectedId}
            onNewChat={() => void handleNewChat()}
            onDelete={(id) => void handleDelete(id)}
            creating={creating}
            touchFriendly={isMobileLayout}
          />
        ) : null}
        <ChatPanel
          conversationId={selectedId}
          conversationTitle={
            msgData?.conversation?.title ?? conversations.find((c) => c.id === selectedId)?.title
          }
          messages={displayMessages}
          models={models}
          selectedModelId={selectedModelId}
          onModelChange={setSelectedModelId}
          loadingMessages={loadingMessages}
          sending={sending}
          onSend={handleSend}
        />
      </div>
    </div>
  );
};

export default AiChatPage;
